// A confirmation dialog, in the page rather than the browser.
//
// NOT window.confirm: a native dialog blocks the whole page, cannot say what
// will actually happen, and cannot be styled to match a dark surface. It also
// gives the reader two words to decide on, where the thing worth showing is the
// consequence — here, that the delete is reversible and destroys nothing.
import React, { useEffect, useRef } from "react";

export default function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel = "Confirm",
  tone = "rose",
  busy = false,
  onConfirm,
  onCancel,
}) {
  const panel = useRef(null);

  // Escape cancels, and focus moves into the dialog so the keyboard works.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    panel.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmTone =
    tone === "rose"
      ? "border-rose-500/50 bg-rose-500/15 text-rose-300 enabled:hover:bg-rose-500/25"
      : "border-sky-500/50 bg-sky-500/15 text-sky-300 enabled:hover:bg-sky-500/25";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4"
      // A click on the backdrop cancels; one inside the panel must not.
      onClick={onCancel}
      role="presentation"
    >
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-xl outline-none"
      >
        <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
        <div className="mt-2 text-sm text-slate-400">{children}</div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-1.5 text-sm text-slate-300 transition enabled:hover:border-slate-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`rounded-lg border px-3 py-1.5 text-sm transition disabled:opacity-50 ${confirmTone}`}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
